import useAuth from '@/hooks/useAuth'
import { useCartState, useCartDispatch } from '@/contexts/CartContext'
import { useRouter } from 'next/router'
import React from 'react'
import toast from 'react-hot-toast'
import CustomToast from '../CustomToast'
import { useQuery } from 'react-query'
import Button from '../Button'
import CartConfirmationModal from './CartConfirmationModal'
import { GetSettings } from '@/services/api/settings'
import { AddressTypes } from '../../types'
import { updateAddressType } from 'CartActions'
import { AddressValues } from '@/forms/Address'
import CouponForm from './CouponForm'
import { useTranslation } from 'next-i18next'
import axios from '@/utils/axios'
import { TailSpin } from 'react-loader-spinner'
import { useCurrency } from '../DesktopHeader'
import useUserPreferences from '@/hooks/useUserPreferences'

type Props = {
  addressType?: AddressTypes
  canProceed?: Boolean
  loadingQuote?: any
  isShippingConfirmed?: boolean
  userCountry?: any
  selectedAddress?: any
  onContinueClick?: () => void
}

export default function CartAside({
  addressType,
  canProceed,
  loadingQuote,
  isShippingConfirmed,
  userCountry,
  selectedAddress,
  onContinueClick,
}: Props) {
  const cart = useCartState()
  const preferences = useUserPreferences()
  const preferenceCurrency = preferences?.currencyCode
  const { symbol } = useCurrency(cart ? cart.currency : preferenceCurrency)
  const { t: tCart } = useTranslation('cart')
  const { t: tAlerts } = useTranslation('alerts')
  const { locale } = useRouter()
  const { data: settings, isLoading: isSettingsLoading } = useQuery(
    ['settings', preferences?.countryCode, preferences?.currencyCode],
    () => GetSettings(preferences?.countryCode, preferences?.currencyCode)
  )

  const cartDispatch = useCartDispatch()
  const { isLoading, user } = useAuth('none')
  const router = useRouter()
  const [isOpen, setIsOpen] = React.useState(false)
  const [isCouponLoading, setIsCouponLoading] = React.useState(false)

  const proceed = async () => {
    if (!user) {
      toast.custom((t) => <CustomToast type='error' t={t} message={tAlerts('login_first')} />, {
        duration: 2500,
      })
      const user_elem = document.querySelector('.user') as HTMLAnchorElement
      user_elem.click()
      return
    }

    if (router.asPath === '/cart' || router.asPath === '/cart#') {
      setIsOpen(true)
      return
    }

    if (router.asPath === '/cart/address' || router.asPath === '/cart/address#') {
      if (!canProceed) {
        let message: string
        if (addressType === 'pickup_point') {
          message = tCart('pleaseSelectPickupPoint') || 'Please select a pickup point before continuing'
        } else if (addressType === 'home_delivery') {
          message = tCart('confirmingShippingOption') || 'Please select an address and confirm your shipping option'
        } else {
          message = tCart('pleaseSelectAddress') || 'Please complete your shipping selection before continuing'
        }
        toast.custom((t) => <CustomToast type='error' t={t} message={message} />, { duration: 2500 })
        return
      }

      if (user?.is_guest && onContinueClick) {
        onContinueClick()
      } else {
        router.push('/cart/payment')
      }
    }
  }

  const deliveryInfoHtml = locale === 'ar' ? settings?.delivery_info?.ar : settings?.delivery_info.en

  return (
    <>
      <div>
        {router.asPath === '/cart' || router.asPath === '/cart#' ? (
          <CouponForm isCouponLoading={isCouponLoading} setIsCouponLoading={setIsCouponLoading} />
        ) : null}
        <div className="cart-info bg-white p-2 py-4 px-5 mt-6 rounded-md" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-md lg:text-lg font-semibold">{tCart('orderSummary')}</h2>
          <ul className="mt-1 lg:mt-2">
            <li className="flex items-center justify-between  mt-1 lg:mt-2">
              <p className="text-gray-400 text-[12px] lg:text-xs">
                {tCart('totalItems')} ({cart.items.length} {tCart('products')})
              </p>
              <strong className="text-[12px] lg:text-xs">{symbol} {cart.subtotal}</strong>
            </li>

            {cart.coupon && (
              <li className="flex items-center justify-between mt-1 lg:mt-2">
                <p className="text-gray-400 text-[12px] lg:text-xs">
                  {tCart('cartDiscountAmount')}
                  <span className="text-red font-semibold">
                    ({typeof cart.coupon === 'string' ? cart.coupon : (cart.coupon as any)?.code || ''})
                  </span>
                </p>
                <strong className="text-[12px] lg:text-xs text-red flex items-center">
                  {symbol}{cart.discount_total}-
                </strong>
              </li>
            )}

            {(router.asPath === '/cart/address' || router.asPath === '/cart/payment') && (
              <li className="flex items-center justify-between mt-1 lg:mt-2">
                <p className="text-gray-400 text-[12px] lg:text-xs">{tCart('shipping')}</p>
                {loadingQuote ? (
                  <TailSpin
                    height="12"
                    width="12"
                    color="#00214D"
                    ariaLabel="tail-spin-loading"
                    radius="1"
                    visible={true}
                  />
                ) : (
                  <strong className="text-[12px] lg:text-xs">{symbol} {cart.shipping_fee}</strong>
                )}
              </li>
            )}

            {router.asPath !== '/cart' && (
              <li className="border-t pt-1 lg:pt-3 flex items-center justify-between mt-2 lg:mt-4">
                <p className="text-gray-400 text-xs">{tCart('totalPrice')}</p>
                <strong className="text-success block font-semibold">
                  {symbol}{cart.total}
                </strong>
              </li>
            )}
          </ul>
          {router.asPath === '/cart' && (
            <p className="text-[12px] lg:text-xs sm:mt-2 lg:mt-5">
              {tCart('note')}
            </p>
          )}
          {!isSettingsLoading && (
            locale === 'ar' ? (
              <p
                className="text-gray-400 text-[12px] lg:text-xs delivery_info mt-2 lg:mt-6"
                dangerouslySetInnerHTML={{ __html: deliveryInfoHtml }}
              />
            ) : (
              <p className="text-gray-400 text-[12px] lg:text-xs delivery_info mt-2 lg:mt-6">
                {tCart('deliveryInfo')}
              </p>
            )
          )}
        </div>

        {router.asPath !== '/cart/payment' && cart.items.length !== 0 ? (
          <div className="mt-5 py-4 font-semibold px-4 rounded-md bg-white proceed-btn">
            <Button
              onClick={() => { proceed() }}
              id='cart-aside'
              type="button"
              classes={['w-full', 'py-3']}
              bg="success">
              <p>{tCart('continue')}</p>
            </Button>
          </div>
        ) : (
          ''
        )}
      </div>
      <CartConfirmationModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  )
}
